import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { useStore } from "../state/state.ts";
import windrose_url from "../assets/windrose.glb";
import floor_plan_mask_url from "../assets/floor_plan_mask_radial.png";
import "./building-model-full.css";

const MEDIAN_VIEW_DISTANCE = 4;
const DEFAULT_VIEW_RADIUS = 8;
const COMPASS_SIZE = 1.2;

// The site plan tile comes from basemap.de, which serves EPSG:3857 only. The
// model origin is EPSG:25832, so the bbox is reprojected before the request.
const SITEPLAN_TILE_TEMPLATE =
  "https://sgx.geodatenzentrum.de/wms_basemapde?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap" +
  "&LAYERS=de_basemapde_web_raster_grau&STYLES=&CRS=EPSG:3857&BBOX={bbox-epsg-3857}" +
  "&WIDTH={width}&HEIGHT={height}&FORMAT=image/png";

// Inverse UTM zone 32N (ETRS89) -> geographic, in radians. GRS80 and WGS84
// differ by far less than a pixel at this scale, so no datum shift is needed.
const utm32n_to_lonlat = (east: number, north: number): [number, number] => {
  const a = 6378137;
  const f = 1 / 298.257222101;
  const k0 = 0.9996;
  const e2 = f * (2 - f);
  const ep2 = e2 / (1 - e2);
  const x = east - 500000;
  const mu =
    north / k0 / (a * (1 - e2 / 4 - (3 * e2 ** 2) / 64 - (5 * e2 ** 3) / 256));
  const e1 = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2));
  const phi1 =
    mu +
    ((3 * e1) / 2 - (27 * e1 ** 3) / 32) * Math.sin(2 * mu) +
    ((21 * e1 ** 2) / 16 - (55 * e1 ** 4) / 32) * Math.sin(4 * mu) +
    ((151 * e1 ** 3) / 96) * Math.sin(6 * mu) +
    ((1097 * e1 ** 4) / 512) * Math.sin(8 * mu);
  const sin_phi1 = Math.sin(phi1);
  const cos_phi1 = Math.cos(phi1);
  const tan_phi1 = Math.tan(phi1);
  const n1 = a / Math.sqrt(1 - e2 * sin_phi1 ** 2);
  const t1 = tan_phi1 ** 2;
  const c1 = ep2 * cos_phi1 ** 2;
  const r1 = (a * (1 - e2)) / (1 - e2 * sin_phi1 ** 2) ** 1.5;
  const d = x / (n1 * k0);
  const lat =
    phi1 -
    ((n1 * tan_phi1) / r1) *
      (d ** 2 / 2 -
        ((5 + 3 * t1 + 10 * c1 - 4 * c1 ** 2 - 9 * ep2) * d ** 4) / 24 +
        ((61 + 90 * t1 + 298 * c1 + 45 * t1 ** 2 - 252 * ep2 - 3 * c1 ** 2) *
          d ** 6) /
          720);
  const lon =
    (9 * Math.PI) / 180 +
    (d -
      ((1 + 2 * t1 + c1) * d ** 3) / 6 +
      ((5 - 2 * c1 + 28 * t1 - 3 * c1 ** 2 + 8 * ep2 + 24 * t1 ** 2) * d ** 5) /
        120) /
      cos_phi1;
  return [lon, lat];
};

// Square bbox in EPSG:3857 around an EPSG:25832 origin, with the half width
// scaled by the Mercator distortion so it still covers `radius_m` on the
// ground.
const siteplan_bbox_3857 = (
  origin: [number, number],
  radius_m: number,
): [number, number, number, number] => {
  const a = 6378137;
  const [lon, lat] = utm32n_to_lonlat(origin[0], origin[1]);
  const x = a * lon;
  const y = a * Math.log(Math.tan(Math.PI / 4 + lat / 2));
  const half = radius_m / Math.cos(lat);
  return [x - half, y - half, x + half, y + half];
};

// The WMS caps how much detail a single request returns, so the ground is
// covered by a 2x2 grid of requests that are stitched into one texture. The
// tiles come back in reading order: north-west, north-east, south-west,
// south-east.
const siteplan_tile_urls = (
  bbox: [number, number, number, number],
  tile_res: number,
) => {
  const [x0, y0, x1, y1] = bbox;
  const xm = (x0 + x1) / 2;
  const ym = (y0 + y1) / 2;
  const quadrants: Array<[number, number, number, number]> = [
    [x0, ym, xm, y1],
    [xm, ym, x1, y1],
    [x0, y0, xm, ym],
    [xm, y0, x1, ym],
  ];
  return quadrants.map((quadrant) =>
    SITEPLAN_TILE_TEMPLATE.replace("{width}", tile_res.toString())
      .replace("{height}", tile_res.toString())
      .replace("{bbox-epsg-3857}", quadrant.join(",")),
  );
};

export default function BuildingModelFull(props?: { url?: string }) {
  const glb_url_store = useStore((state) => state.glb_url);
  const context_glb_url = useStore((state) => state.context_glb_url);
  const parcel_buildings = useStore((state) => state.parcel_buildings);
  const building_url = props?.url ?? glb_url_store;
  const loading_building_data = useStore((state) => state.loading_building_data);
  const siteplan = useStore((state) => state.siteplan);

  const container_ref = useRef<HTMLDivElement>(null);
  const camera_ref = useRef<THREE.PerspectiveCamera | null>(null);
  const controls_ref = useRef<OrbitControls | null>(null);
  const default_view_ref = useRef<{
    position: THREE.Vector3;
    target: THREE.Vector3;
  } | null>(null);

  const idle_timeout_ref = useRef<number | null>(null);
  const pause_auto_rotate = useRef(() => {});
  const [status, setStatus] = useState<"idle" | "loading" | "loaded" | "error">(
    building_url ? "loading" : "idle",
  );

  const reset_view = () => {
    pause_auto_rotate.current();
    const camera = camera_ref.current;
    const controls = controls_ref.current;
    const view = default_view_ref.current;
    if (!camera || !controls || !view) return;
    camera.position.copy(view.position);
    controls.target.copy(view.target);
    controls.update();
  };

  const top_view = () => {
    pause_auto_rotate.current();
    const camera = camera_ref.current;
    const controls = controls_ref.current;
    const view = default_view_ref.current;
    if (!camera || !controls || !view) return;
    const distance = view.position.distanceTo(view.target) * 2.5;
    controls.target.copy(view.target);
    camera.position.set(
      view.target.x,
      view.target.y + distance,
      view.target.z + distance * 0.001,
    );
    
    camera.fov = 10;
    camera.updateProjectionMatrix();

    controls.update();
  };

  const scene_api_ref = useRef<{
    load: (url: string) => void;
    unload: () => void;
  } | null>(null);

  const load_inputs_ref = useRef({ context_glb_url, parcel_buildings, siteplan });
  load_inputs_ref.current = { context_glb_url, parcel_buildings, siteplan };

  useEffect(() => {
    const container = container_ref.current;
    if (!container) return;

    const width = container.clientWidth || 1;
    const height = container.clientHeight || 1;

    const scene = new THREE.Scene();
    //scene.fog = new THREE.FogExp2(0xFFFFFF, 0.1);

    const camera = new THREE.PerspectiveCamera(10, width / height, 0.1, 10000);
    // On wide containers the model is shifted right to leave room for the
    // data overlay on the left; on narrow ones it stays centered.
    const WIDE_BREAKPOINT_PX = 767;
    const apply_view_offset = (w: number, h: number) => {
      const offset_x = w > WIDE_BREAKPOINT_PX ? -w / 6 : 0;
      camera.setViewOffset(w, h, offset_x, 0, w, h);
    };
    apply_view_offset(width, height);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    renderer.autoClear = false;
    container.appendChild(renderer.domElement);

    const overlay_scene = new THREE.Scene();
    const overlay_camera = new THREE.OrthographicCamera(-1.3, 1.3, 1.3, -1.3, 0.1, 100);
    const overlay_size = 120 * COMPASS_SIZE;
    const overlay_margin_top = -18;
    const overlay_margin_right = -18;
    
    const key_light = new THREE.DirectionalLight(0xffffff, 2.0);
    key_light.position.set(1, 2, 1.5);
    scene.add(key_light);
    const fill_light = new THREE.DirectionalLight(0xffffff, 0.6);
    fill_light.position.set(-1, 0.5, -1);
    scene.add(fill_light);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.enablePan = false;
    controls.maxPolarAngle = Math.PI / 2 - 0.1;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.5;

    controls.touches = {
      ONE: THREE.TOUCH.PAN,
      TWO: THREE.TOUCH.DOLLY_ROTATE,
    };
    renderer.domElement.style.touchAction = "pan-y";

    const IDLE_DELAY_MS = 4000;
    const clear_idle_timeout = () => {
      if (idle_timeout_ref.current !== null) {
        window.clearTimeout(idle_timeout_ref.current);
        idle_timeout_ref.current = null;
      }
    };
    const pause_rotation = () => {
      controls.autoRotate = false;
      clear_idle_timeout();
    };
    const resume_rotation_when_idle = () => {
      clear_idle_timeout();
      idle_timeout_ref.current = window.setTimeout(() => {
        idle_timeout_ref.current = null;
        controls.autoRotate = true;
      }, IDLE_DELAY_MS);
    };
    pause_auto_rotate.current = () => {
      pause_rotation();
      resume_rotation_when_idle();
    };
    controls.addEventListener("start", pause_rotation);
    controls.addEventListener("end", resume_rotation_when_idle);

    camera_ref.current = camera;
    controls_ref.current = controls;

    let disposed = false;
    let frame_id = 0;
    let model: THREE.Object3D | undefined;
    let context_model: THREE.Object3D | undefined;
    let windrose: THREE.Object3D | undefined;
    // Guards loader callbacks against a model swap that happened meanwhile.
    let load_token = 0;
    // The camera is framed on the first model only; later swaps keep the
    // orbit the user has set up.
    let has_framed = false;
    const FLOOR_PLAN_FADE_MS = 1000;
    const FLOOR_PLAN_MAX_OPACITY = 0.6;
    const FLOOR_PLAN_RADIUS = 150;
    // One crossfading ground layer: the grid, or a site plan tile.
    type FloorLayer = {
      mesh: THREE.Mesh;
      material: THREE.MeshBasicMaterial;
      // Opacity the layer is fading towards, 0 or FLOOR_PLAN_MAX_OPACITY.
      target: number;
    };
    const floor_layers: FloorLayer[] = [];
    const windrose_holder = new THREE.Group();
    overlay_scene.add(windrose_holder);

    // Drop animation: on the way in the main building falls first and the
    // context buildings follow, staggered by their distance from the scene
    // center; on the way out the same order runs backwards.
    const DROP_HEIGHT = 100;
    const DROP_DURATION_MS = 1200;
    const DROP_STAGGER_MS = 900;
    // Head start of the main building over the nearest context building.
    const MAIN_DROP_LEAD_MS = 600;
    const DROP_OUT_TOTAL_MS =
      DROP_STAGGER_MS + MAIN_DROP_LEAD_MS + DROP_DURATION_MS;
    type Tween = {
      object: THREE.Object3D;
      from_y: number;
      to_y: number;
      start_ms: number;
      // Out-tweens accelerate away (easeInCubic), in-tweens settle (easeOutCubic).
      ease_in: boolean;
    };
    // Models on their way out, removed and disposed once their tweens finish.
    type Retiring = {
      objects: THREE.Object3D[];
      start_ms: number;
      remove_at_ms: number;
    };
    let tweens: Tween[] = [];
    let retiring: Retiring[] = [];
    let main_drop_start: number | null = null;
    let pending_context_root: THREE.Object3D | null = null;

    // Horizontal distance of each child from the root's center, which drives
    // the stagger in both directions.
    const child_distances = (root: THREE.Object3D) => {
      const center = new THREE.Box3()
        .setFromObject(root)
        .getCenter(new THREE.Vector3());
      const object_center = new THREE.Vector3();
      return root.children.map((child) => {
        new THREE.Box3().setFromObject(child).getCenter(object_center);
        return Math.hypot(
          object_center.x - center.x,
          object_center.z - center.z,
        );
      });
    };

    // The main building drops as one piece — staggering its roof and wall
    // parts individually would tear the model apart.
    const start_main_drop_in = (root: THREE.Object3D) => {
      main_drop_start = performance.now();
      const to_y = root.position.y;
      root.position.y = to_y + DROP_HEIGHT;
      tweens.push({
        object: root,
        from_y: to_y + DROP_HEIGHT,
        to_y,
        start_ms: main_drop_start,
        ease_in: false,
      });
      if (pending_context_root) {
        const parked = pending_context_root;
        pending_context_root = null;
        start_context_drop_in(parked, true);
      }
    };

    const start_context_drop_in = (root: THREE.Object3D, parked = false) => {
      if (!root.children.length) return;
      if (main_drop_start === null) {
        // The main building has not loaded yet; park the context out of sight
        // and let its drop start once the main one is under way.
        root.children.forEach((child) => {
          child.position.y += DROP_HEIGHT;
        });
        pending_context_root = root;
        return;
      }

      const distances = child_distances(root);
      const max_distance = Math.max(...distances) || 1;
      const base_start = Math.max(
        performance.now(),
        main_drop_start + MAIN_DROP_LEAD_MS,
      );

      root.children.forEach((child, index) => {
        // Parked children were already raised; raising again would double it.
        const to_y = parked ? child.position.y - DROP_HEIGHT : child.position.y;
        child.position.y = to_y + DROP_HEIGHT;
        tweens.push({
          object: child,
          from_y: to_y + DROP_HEIGHT,
          to_y,
          start_ms:
            base_start + (distances[index] / max_distance) * DROP_STAGGER_MS,
          ease_in: false,
        });
      });
    };

    // Reverse of the entry: the outermost context buildings leave first, the
    // main building last.
    const start_drop_out = () => {
      const now = performance.now();
      const objects: THREE.Object3D[] = [];
      // Anything still dropping in is superseded by the exit.
      tweens = [];
      main_drop_start = null;
      pending_context_root = null;

      const push_out = (object: THREE.Object3D, delay_ms: number) => {
        objects.push(object);
        tweens.push({
          object,
          from_y: object.position.y,
          to_y: object.position.y + DROP_HEIGHT,
          start_ms: now + delay_ms,
          ease_in: true,
        });
      };

      if (context_model && context_model.children.length) {
        const distances = child_distances(context_model);
        const max_distance = Math.max(...distances) || 1;
        context_model.children.forEach((child, index) => {
          push_out(
            child,
            ((max_distance - distances[index]) / max_distance) *
              DROP_STAGGER_MS,
          );
        });
      }
      if (model) push_out(model, DROP_STAGGER_MS + MAIN_DROP_LEAD_MS);

      // Fading a model out is not an interaction, so the camera keeps
      // orbiting; a pause from an actual interaction has an idle timer
      // pending and stays untouched.
      if (idle_timeout_ref.current === null) controls.autoRotate = true;

      // The ground stays; the site plan tile fades out while the grid fades
      // back in on the same clock.
      wanted_siteplan_key = null;
      show_floor_layer(grid_layer);

      const roots = [model, context_model].filter(Boolean) as THREE.Object3D[];
      if (!roots.length) return;

      retiring.push({
        // Keep the roots around so they can be removed from the scene, even
        // though the tweens move their children.
        objects: roots,
        start_ms: now,
        remove_at_ms: now + DROP_OUT_TOTAL_MS,
      });
      model = undefined;
      context_model = undefined;
    };

    const update_tweens = (now: number) => {
      if (tweens.length) {
        let done = true;
        for (const tween of tweens) {
          const progress = Math.min(
            Math.max((now - tween.start_ms) / DROP_DURATION_MS, 0),
            1,
          );
          const eased = tween.ease_in
            ? Math.pow(progress, 3)
            : 1 - Math.pow(1 - progress, 3);
          tween.object.position.y =
            tween.from_y + (tween.to_y - tween.from_y) * eased;
          if (progress < 1) done = false;
        }
        if (done) tweens = [];
      }

      if (!retiring.length) return;
      const still_retiring: Retiring[] = [];
      for (const entry of retiring) {
        if (now < entry.remove_at_ms) {
          still_retiring.push(entry);
          continue;
        }
        for (const object of entry.objects) {
          scene.remove(object);
          dispose_object(object);
        }
      }
      retiring = still_retiring;
    };

    // Places the camera on the default three-quarter view for a model of the
    // given bounding radius and records it as the "Perspektive" reset view.
    // `keep_azimuth` only re-fits the distance and leaves the orbit angle
    // alone, so a model swap does not interrupt the running auto-rotation.
    const apply_default_view = (radius: number, keep_azimuth = false) => {
      const fov = (camera.fov * Math.PI) / 180;
      const distance = (radius / Math.sin(fov / 2)) * MEDIAN_VIEW_DISTANCE;
      const target = new THREE.Vector3(0, 5, 0);
      const default_position = new THREE.Vector3(
        distance,
        distance * 0.7,
        distance,
      );

      if (keep_azimuth) {
        const offset = camera.position.clone().sub(controls.target);
        const azimuth = Math.atan2(offset.x, offset.z);
        const default_offset = default_position.clone().sub(target);
        const ground_distance = Math.hypot(default_offset.x, default_offset.z);
        camera.position.set(
          target.x + Math.sin(azimuth) * ground_distance,
          target.y + default_offset.y,
          target.z + Math.cos(azimuth) * ground_distance,
        );
      } else {
        camera.position.copy(default_position);
      }
      camera.near = distance / 100;
      camera.far = distance * 100;
      camera.updateProjectionMatrix();


      controls.target.copy(target);
      controls.maxDistance = distance * 2;
      controls.minDistance = distance * 0.75;
      controls.update();

      // The reset view stays the canonical angle, whatever the orbit is at.
      default_view_ref.current = {
        position: default_position,
        target: target.clone(),
      };
    };

    const frame_object = (object: THREE.Object3D) => {
      const box = new THREE.Box3().setFromObject(object);
      const size = box.getSize(new THREE.Vector3());

      apply_default_view(Math.max(size.x, size.y, size.z) * 0.5 || 1, true);
    };

    // Until the first model arrives the camera already sits on the default
    // view, sized for a typical building, so nothing jumps on load.
    apply_default_view(DEFAULT_VIEW_RADIUS);

    const roof_name_pattern = /^roof_\d+$/;
    const roof_material = new THREE.MeshStandardMaterial({ color: 0xff5533 });

    const wall_name_pattern = /^wall_\d+$/;
    // Walls pick up the app's accent color; read from the container so a theme
    // override anywhere up the tree wins over the :root default.
    const wall_material = new THREE.MeshStandardMaterial({
      color: read_css_color(container, "--neutral", 0xaaaaaa),
    });

    const stripe_texture = create_stripe_texture();

    // Alpha mask for the ground layers; `alphaMap` samples the green channel,
    // so the texture stays in linear space (no sRGB conversion).
    const floor_plan_alpha_texture = new THREE.TextureLoader().load(
      floor_plan_mask_url,
      () => {
        if (disposed) return;
        floor_layers.forEach((layer) => (layer.material.needsUpdate = true));
      },
      undefined,
      (error) => {
        if (disposed) return;
        console.error("BuildingModel: failed to load floor plan mask", error);
      },
    );
    
    // Fallback ground texture, shown whenever no site plan tile is available.
    // The plane is 300 m wide, so six tiles give 50 m squares with 10 m cells.
    const grid_texture = create_grid_texture(6);

    // The ground is built from two coplanar layers that always fade in
    // opposite directions on the same clock: the grid carries the scene while
    // no site plan tile is shown, the site plan takes over once its tile has
    // loaded and hands back on unload.
    const create_floor_layer = (
      map: THREE.Texture | null,
      y: number,
      render_order: number,
    ) => {
      const geometry = new THREE.PlaneGeometry(
        FLOOR_PLAN_RADIUS * 2,
        FLOOR_PLAN_RADIUS * 2,
      );
      const material = new THREE.MeshBasicMaterial({
        map,
        alphaMap: floor_plan_alpha_texture,
        transparent: true,
        depthWrite: false,
        opacity: 0,
      });
      // Ground tiles are desaturated in the shader so they read as a neutral
      // backdrop behind the colored buildings.
      material.onBeforeCompile = (shader) => {
        shader.fragmentShader = shader.fragmentShader.replace(
          "#include <map_fragment>",
          `#include <map_fragment>
           diffuseColor.rgb = vec3(
             dot(diffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722))
           );`,
        );
      };
      // Keeps the patched program from sharing a cache entry with an
      // otherwise identical unpatched MeshBasicMaterial.
      material.customProgramCacheKey = () => "floor_plan_grayscale";

      const mesh = new THREE.Mesh(geometry, material);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.y = y;
      mesh.renderOrder = render_order;
      mesh.visible = false;
      scene.add(mesh);
      const layer = { mesh, material, target: 0 };
      floor_layers.push(layer);
      return layer;
    };

    const grid_layer = create_floor_layer(grid_texture, -0.05, 0);
    // The site plan sits a hair above the grid so the two never z-fight.
    const siteplan_layer = create_floor_layer(null, -0.049, 1);

    // Fades the given layer in and every other one out, both at the same rate.
    const show_floor_layer = (layer: FloorLayer) => {
      floor_layers.forEach((l) => {
        l.target = l === layer ? FLOOR_PLAN_MAX_OPACITY : 0;
      });
    };

    const update_floor_layers = (delta_ms: number) => {
      const step = (delta_ms / FLOOR_PLAN_FADE_MS) * FLOOR_PLAN_MAX_OPACITY;
      for (const layer of floor_layers) {
        const current = layer.material.opacity;
        if (current === layer.target) continue;
        layer.material.opacity =
          current < layer.target
            ? Math.min(current + step, layer.target)
            : Math.max(current - step, layer.target);
        layer.mesh.visible = layer.material.opacity > 0;
      }
    };

    // Site plan tiles are kept so switching back to an address does not
    // refetch them, and so the current one can still fade out after its model
    // is gone.
    type SiteplanTile = { texture: THREE.Texture; loaded: boolean };
    const siteplan_tiles = new Map<string, SiteplanTile>();
    // Tile the ground should show; null while no model is loaded, so a tile
    // that only arrives after the model left does not pop back in.
    let wanted_siteplan_key: string | null = null;

    const apply_siteplan_tile = (key: string) => {
      const tile = siteplan_tiles.get(key);
      if (!tile || !tile.loaded || wanted_siteplan_key !== key) return;
      siteplan_layer.material.map = tile.texture;
      siteplan_layer.material.needsUpdate = true;
      show_floor_layer(siteplan_layer);
    };

    // Loads the 2x2 grid of WMS images and stitches them into a single
    // texture, so the ground keeps using one material and one draw call.
    const show_siteplan_tile = (urls: string[], tile_res: number) => {
      const key = urls.join("|");
      wanted_siteplan_key = key;
      // A tile that is still loading applies itself from its own callback.
      if (siteplan_tiles.has(key)) {
        apply_siteplan_tile(key);
        return;
      }
      const canvas = document.createElement("canvas");
      canvas.width = tile_res * 2;
      canvas.height = tile_res * 2;
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      siteplan_tiles.set(key, { texture, loaded: false });

      const context = canvas.getContext("2d");
      let pending = urls.length;
      urls.forEach((url, index) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => {
          if (disposed || !siteplan_tiles.has(key)) return;
          context?.drawImage(
            image,
            (index % 2) * tile_res,
            Math.floor(index / 2) * tile_res,
            tile_res,
            tile_res,
          );
          pending -= 1;
          if (pending > 0) return;
          const tile = siteplan_tiles.get(key);
          if (tile) tile.loaded = true;
          texture.needsUpdate = true;
          apply_siteplan_tile(key);
        };
        image.onerror = (error) => {
          // One missing quadrant would leave a blank quarter, so the whole
          // tile is dropped and can be retried on the next model load.
          siteplan_tiles.delete(key);
          texture.dispose();
          if (disposed) return;
          console.error("BuildingModel: failed to load site plan texture", error);
        };
        image.src = url;
      });
    };

    // Nothing is loaded yet, so the grid comes up first.
    show_floor_layer(grid_layer);

    const windrose_material = new THREE.MeshBasicMaterial({ 
      color: 0x000000,
    });

    const windrose_north_material = new THREE.MeshBasicMaterial({ 
      color: 0xff0000,
    });

     const windrose_sphere_material = new THREE.MeshBasicMaterial({ 
      color: 0xffffff,
      opacity: 0.75,
      transparent: true,
    });

    const below_grade_material = new THREE.MeshStandardMaterial({
      color: 0x808080
      //map: stripe_texture,
    });
    
    const parcel_building_material = new THREE.MeshStandardMaterial({ 
      color: 0x5555bb,
    });

    const context_building_material = new THREE.MeshStandardMaterial({ 
      color: 0xffffff,
    });

    // Shared across every loaded model, so retiring one must not dispose them.
    const shared_materials = new Set<THREE.Material>([
      roof_material,
      wall_material,
      below_grade_material,
      windrose_material,
      windrose_north_material,
      windrose_sphere_material,
      parcel_building_material,
      context_building_material,
    ]);

    const dispose_object = (object: THREE.Object3D | undefined) => {
      if (!object) return;
      object.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        const material = mesh.material as
          | THREE.Material
          | THREE.Material[]
          | undefined;
        const materials = Array.isArray(material)
          ? material
          : material
            ? [material]
            : [];
        materials.forEach((m) => {
          if (!shared_materials.has(m)) m.dispose();
        });
      });
    };

    const loader = new GLTFLoader();

    const load_windrose = () => {
      if (windrose) return;
      loader.load(
        windrose_url,
        (gltf) => {
          if (disposed) return;
          windrose = gltf.scene;
          windrose.traverse((child) => {
            const mesh = child as THREE.Mesh;
              if (mesh.isMesh) {
                
                if(child.name == "north" || child.name == "north_text") {
                  mesh.material = windrose_north_material;
                }else if(child.name == "compass_sphere"){
                   mesh.material = windrose_sphere_material;
                }else{
                  mesh.material = windrose_material;
                }
                mesh.renderOrder = 1;
              }
          });

          const box = new THREE.Box3().setFromObject(windrose);
          const center = box.getCenter(new THREE.Vector3());
          const radius = box.getBoundingSphere(new THREE.Sphere()).radius || 1;
          windrose.position.sub(center);
          windrose_holder.scale.setScalar(1 / radius * COMPASS_SIZE);
          windrose_holder.add(windrose);
        },
        undefined,
        (error) => {
          if (disposed) return;
          console.error("BuildingModel: failed to load windrose GLB", error);
        },
      );
    };

    const load_models = (building_url: string) => {
      const token = ++load_token;
      const { context_glb_url, parcel_buildings, siteplan } =
        load_inputs_ref.current;

      load_windrose();

      loader.load(
        building_url,
        (gltf) => {
          if (disposed || token !== load_token) return;
          model = gltf.scene;
          scene.add(model);

          model.traverse((obj) => {
            const mesh = obj as THREE.Mesh;
            if (mesh.isMesh && mesh.geometry && !mesh.geometry.attributes.normal) {
              mesh.geometry.computeVertexNormals();
            }
          });

          model.traverse((obj) => {
            if (!roof_name_pattern.test(obj.name)) return;
            obj.traverse((child) => {
              const mesh = child as THREE.Mesh;
              if (mesh.isMesh) mesh.material = roof_material;
            });
          });

          model.traverse((obj) => {
            if (!wall_name_pattern.test(obj.name)) return;
            obj.traverse((child) => {
              const mesh = child as THREE.Mesh;
              if (mesh.isMesh) mesh.material = wall_material;
            });
          });

          model.traverse((obj) => {
            if (obj.name !== "below_grade") return;
            obj.traverse((child) => {
              const mesh = child as THREE.Mesh;
              if (mesh.isMesh) mesh.material = below_grade_material;
            });
          });

          // A site plan tile takes over the ground when one is configured and
          // the model carries a georeference; otherwise the grid stays.
          const origin = gltf.scene.userData.origin_epsg25832;
          if (siteplan && origin) {
            const r = FLOOR_PLAN_RADIUS;
            // Per quadrant, so the stitched texture is twice that.
            const res = 512;
            const bbox = siteplan_bbox_3857(origin, r);

            show_siteplan_tile(siteplan_tile_urls(bbox, res), res);
          }

          if (!has_framed) {
            has_framed = true;
            frame_object(model);
          }
          start_main_drop_in(model);
          setStatus("loaded");
        },
        undefined,
        (error) => {
          if (disposed || token !== load_token) return;
          console.error("BuildingModel: failed to load GLB", building_url, error);
          setStatus("error");
        },
      );

      if(context_glb_url){
        loader.load(
          context_glb_url,
          (gltf) => {
            if (disposed || token !== load_token) return;
            context_model = gltf.scene;
            scene.add(context_model);
            if(parcel_buildings){
              const parcel_buildings_names = parcel_buildings.map(pb => pb.building_id);
              context_model.traverse((obj) => {
                if(parcel_buildings_names.includes(obj.name)){
                  obj.traverse((child) => {
                    const mesh = child as THREE.Mesh;
                    if (mesh.isMesh) mesh.material = parcel_building_material;
                  });
                }else{
                   obj.traverse((child) => {
                    const mesh = child as THREE.Mesh;
                    if (mesh.isMesh) mesh.material = context_building_material;
                  });
                }
              });
            }
            start_context_drop_in(context_model);
          },
          undefined,
          (error) => {
            if (disposed || token !== load_token) return;
            console.error("BuildingModel: failed to load GLB", context_glb_url, error);
            setStatus("error");
          },
        );
      }
    };

    const unload_models = () => {
      // Drops any in-flight loads of the outgoing address.
      load_token++;
      start_drop_out();
    };

    scene_api_ref.current = { load: load_models, unload: unload_models };

    //onst geo = new THREE.PlaneGeometry(150, 150);
    const orbit_direction = new THREE.Vector3();

    let last_frame_ms = performance.now();

    const render = () => {
      const now = performance.now();
      const delta_ms = now - last_frame_ms;
      last_frame_ms = now;

      controls.update();
      update_tweens(now);
      update_floor_layers(delta_ms);

      const w = container.clientWidth || 1;
      const h = container.clientHeight || 1;

      renderer.setScissorTest(false);
      renderer.setViewport(0, 0, w, h);
      renderer.clear();
      renderer.render(scene, camera);

      // Compass HUD: same viewing direction as the main camera, fixed size,
      // pinned to the top-left corner of the canvas. Only shown while there is
      // a model to orient — including one that is still tweening out.
      if (model || context_model || retiring.length) {
        orbit_direction.copy(camera.position).sub(controls.target).normalize();
        overlay_camera.position.copy(orbit_direction).multiplyScalar(10);
        overlay_camera.up.copy(camera.up);
        overlay_camera.lookAt(0, 0, 0);

        const x = w - (overlay_size + overlay_margin_right);
        const y = h - (overlay_size + overlay_margin_top);
        renderer.setScissorTest(true);
        renderer.setScissor(x, y, overlay_size, overlay_size);
        renderer.setViewport(x, y, overlay_size, overlay_size);
        renderer.clearDepth();
        renderer.render(overlay_scene, overlay_camera);
        renderer.setScissorTest(false);
      }

      frame_id = requestAnimationFrame(render);
    };
    render();

    const handle_resize = () => {
      const w = container.clientWidth || 1;
      const h = container.clientHeight || 1;
      camera.aspect = w / h;
      apply_view_offset(w, h);
      renderer.setSize(w, h);
    };

    const resize_observer = new ResizeObserver(handle_resize);
    resize_observer.observe(container);
    window.addEventListener("resize", handle_resize);

    return () => {
      disposed = true;
      scene_api_ref.current = null;
      cancelAnimationFrame(frame_id);
      resize_observer.disconnect();
      window.removeEventListener("resize", handle_resize);
      clear_idle_timeout();
      pause_auto_rotate.current = () => {};
      controls.removeEventListener("start", pause_rotation);
      controls.removeEventListener("end", resume_rotation_when_idle);
      controls.dispose();
      camera_ref.current = null;
      controls_ref.current = null;
      default_view_ref.current = null;
      dispose_object(model);
      dispose_object(context_model);
      dispose_object(windrose);
      floor_layers.forEach((layer) => dispose_object(layer.mesh));
      retiring.forEach((entry) => {
        entry.objects.forEach(dispose_object);
      });
      retiring = [];
      shared_materials.forEach((material) => material.dispose());
      stripe_texture.dispose();
      floor_plan_alpha_texture.dispose();
      grid_texture.dispose();
      siteplan_tiles.forEach((tile) => tile.texture.dispose());
      siteplan_tiles.clear();
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  useEffect(() => {
    const api = scene_api_ref.current;
    if (!api) return;

    setStatus(building_url ? "loading" : "idle");
    if (building_url) api.load(building_url);

    // The previous model is not removed here — it tweens out and disposes
    // itself once the animation has run.
    return () => {
      scene_api_ref.current?.unload();
    };
  }, [building_url]);

  return (
    <div className="building-model-full__container">
      {/* {status == "loaded" && <div className="building-model-full__sidebar"><BuildingGeometryData/></div>} */}
      {status == "loaded" &&
        <div className="building-model-full__view-buttons">
          <button
            type="button"
            className="building-model-full__view-button"
            onClick={reset_view}
            aria-label="Kamera auf Startperspektive zurücksetzen"
          >
            Perspektive
          </button>
          <button
            type="button"
            className="building-model-full__view-button"
            onClick={top_view}
            aria-label="Kamera auf Vogelperspektive von oben setzen"
          >
            Lageplan
          </button>
        </div>
      }
      <div className="building-model-full__overlay"></div>
      <div className="building-model-full" ref={container_ref}>
        <div className={"building-model-full-status " + ( (status === "idle" || status === "loading") && "building-model-full-status--visible")}>
          <span className="building-model-full-status__inner">{
            (loading_building_data || status === "loading") ? 
            <span>Vorschau wird geladen…</span> :  (status == "loaded" ? 
              <></> : 
              <span>Keine Vorschau verfügbar. <br></br> (Nicht notwendig um Fortzufahren)</span>
            )
            }   
          </span>
        </div>
      </div>
    </div>
  );
}

// Resolves a CSS custom property to a THREE color. CSS colors are sRGB, so
// they need the explicit color space to survive three's color management.
function read_css_color(
  element: HTMLElement,
  variable: string,
  fallback: number,
): THREE.Color {
  const value = getComputedStyle(element).getPropertyValue(variable).trim();
  if (!value) return new THREE.Color(fallback);
  try {
    return new THREE.Color().setStyle(value, THREE.SRGBColorSpace);
  } catch {
    console.warn("BuildingModel: unsupported color for", variable, value);
    return new THREE.Color(fallback);
  }
}

function create_stripe_texture(): THREE.Texture {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = "#c8c8c8";
  ctx.fillRect(0, 0, size, size);

  ctx.save();
  ctx.translate(size / 2, size / 2);
  ctx.rotate(Math.PI / 4);
  ctx.translate(-size, -size);
  ctx.fillStyle = "#555555";
  const stripe_width = size / 8;
  for (let y = 0; y < size * 2; y += stripe_width * 2) {
    ctx.fillRect(0, y, size * 2, stripe_width);
  }
  ctx.restore();

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(4, 4);
  return texture;
}

function create_grid_texture(tiles: number): THREE.Texture {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = "#efefef";
  ctx.fillRect(0, 0, size, size);

  ctx.strokeStyle =  "#bebebeaa";
  ctx.lineWidth = 1;
  ctx.beginPath();

  const minor_cell_count = 4
  for (let i = 1; i < minor_cell_count; i++) {
    const p = (size / minor_cell_count) * i;
    ctx.moveTo(p, 0);
    ctx.lineTo(p, size);
    ctx.moveTo(0, p);
    ctx.lineTo(size, p);
  }
  ctx.stroke();

  ctx.strokeStyle = "#bebebe";
  ctx.lineWidth = 2;
  ctx.strokeRect(0, 0, size, size);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.generateMipmaps = true;
  // Nearest sampling keeps the grid lines crisp instead of smearing them.
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestMipmapNearestFilter;
  texture.repeat.set(tiles, tiles);
  return texture;
}
